import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import ripsTitleImage from '../../../../assets/rips/crown-rips-text.png';
import ripsPacksImage from '../../../../assets/rips/rips_packs_widget.png';
import ripsPacksWithTextImage from '../../../../assets/rips/rips_packs_widget_with_text.png';
import Button from '../../../../components/buttons/Button';
import { Loader } from '../../../../components/Loader/Loader';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useStores } from '../../../../stores/useStores';
import type { CrownRipsWidgetDataType, WidgetSizeType } from '../../types';
import { GeneralWideWidget } from '../GeneralWidget';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import { PENDING_PACKS_DISPLAY_CAP, WIDGET_PULSE_DURATION_MS } from './constants';
import styles from './CrownRipsWidget.module.scss';

interface CrownRipsWidgetProps {
  sizeType: WidgetSizeType;
  data: CrownRipsWidgetDataType;
}

function CrownRipsWidgetComponent({ data, sizeType }: CrownRipsWidgetProps) {
  const { crownRipsStore } = useStores();
  const { t } = useTranslation();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [isPulsing, setIsPulsing] = useState(false);

  // One-shot arrival pulse when a claimed ripsPack's fly-celebration lands here (see
  // RipsPackClaimFlyHost.triggerWidgetPulse). Nonce-driven, not a boolean the store would have to
  // remember to reset, so a second arrival before the first pulse finishes still re-triggers cleanly.
  const { widgetPulseNonce } = crownRipsStore;
  // The nonce lives in the app-lifetime store but this widget remounts on every Lobby visit — track
  // the nonce we last reacted to in a ref (seeded with whatever the store already holds at mount) so
  // a remount with a stale/nonzero nonce doesn't replay the pulse; only a genuine change fires it.
  const lastPulsedNonceRef = useRef(widgetPulseNonce);
  useEffect(() => {
    if (widgetPulseNonce === lastPulsedNonceRef.current) {
      return;
    }
    lastPulsedNonceRef.current = widgetPulseNonce;
    if (prefersReducedMotion) {
      return;
    }
    setIsPulsing(true);
    const timeout = setTimeout(() => setIsPulsing(false), WIDGET_PULSE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [widgetPulseNonce, prefersReducedMotion]);

  // Launch does an async resolve + launch-game round-trip before the game-ui dialog opens; show a
  // full-screen loader for that gap so the tap has immediate feedback (and can't be double-fired).
  const onClick = () => {
    if (crownRipsStore.isLoadingGame) return;
    void crownRipsStore.startCrownRipsGame();
  };

  const isSmall = sizeType === 'small';
  const openLabel = t('common.open');

  const { pendingPacksCount } = crownRipsStore;
  const hasPendingPacks = pendingPacksCount > 0;
  const pendingPacksDisplay =
    pendingPacksCount > PENDING_PACKS_DISPLAY_CAP ? `${PENDING_PACKS_DISPLAY_CAP}+` : String(pendingPacksCount);

  return (
    <>
      {/* Narrow: the captioned packs art ("CROWN RIPS" baked in) over an OPEN pill. Wide: the plain
          packs art in the shared wide shell, with the title rendered beside it. */}
      <WidgetWrapper
        className={classNames(styles.widgetRoot, { [styles.pulse]: isPulsing })}
        dataTestId={data.dataTestId}
        sizeType={sizeType}
        smallWidgetExtended={isSmall}
        onClick={onClick}
      >
        {isSmall ? (
          <div className={styles.small}>
            <img alt="" className={styles.smallImg} src={ripsPacksWithTextImage} />
            <Button className={styles.smallBtn}>{openLabel}</Button>
          </div>
        ) : (
          <GeneralWideWidget
            ctaLabel={openLabel}
            title={<img alt="Crown Rips" className={styles.wideTitle} src={ripsTitleImage} />}
            widgetImageUrl={ripsPacksImage}
          />
        )}
        {hasPendingPacks && (
          // key={pendingPacksDisplay}: remounts the badge whenever the displayed number changes (not
          // just on first appearance), so the spring pop-in replays on every count change too, per
          // the product ask. initial={false} under reduced motion skips the entrance transition
          // entirely — the badge just appears/updates already at rest.
          <motion.div
            key={pendingPacksDisplay}
            animate={{ scale: 1 }}
            aria-label={t('crownRips.pendingPacksBadge', { count: pendingPacksCount })}
            className={styles.pendingPacksBadge}
            initial={prefersReducedMotion ? false : { scale: 0 }}
            role="status"
            transition={{ type: 'spring', stiffness: 500, damping: 15 }}
          >
            {pendingPacksDisplay}
          </motion.div>
        )}
      </WidgetWrapper>
      {crownRipsStore.isLoadingGame && <Loader fullScreen />}
    </>
  );
}

export const CrownRipsWidget = observer(CrownRipsWidgetComponent);
